(function () {
  'use strict';

  if (window.wigglyHomeReimaginedLoaded) return;
  window.wigglyHomeReimaginedLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initialiseHero(hero) {
    if (!hero || hero.dataset.homeHeroReady === 'true') return;

    const slider = hero.querySelector('.home-concept-hero__swiper');
    const slides = slider?.querySelectorAll('.swiper-slide') || [];
    if (!slider || slides.length < 2 || typeof window.Swiper !== 'function') {
      hero.dataset.homeHeroReady = 'true';
      return;
    }

    const dots = hero.querySelector('[data-home-hero-dots]');
    hero.homeHeroSwiper = new window.Swiper(slider, {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      autoplay: reducedMotion.matches ? false : {
        delay: 4000,
        disableOnInteraction: false,
        pauseOnMouseEnter: false
      },
      loop: true,
      speed: reducedMotion.matches ? 0 : 900,
      allowTouchMove: false,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      pagination: dots ? { el: dots, clickable: false } : undefined
    });

    hero.dataset.homeHeroReady = 'true';
  }

  function initialiseShelf(shelf) {
    if (!shelf || shelf.dataset.homeShelfReady === 'true') return;

    const viewport = shelf.querySelector('[data-home-shelf-viewport]');
    const previous = shelf.querySelector('[data-home-shelf-button="previous"]');
    const next = shelf.querySelector('[data-home-shelf-button="next"]');
    if (!viewport) return;

    shelf.dataset.homeShelfReady = 'true';
    viewport.scrollLeft = 0;

    function update() {
      const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      // Scroll snap can settle a shelf a few pixels away from zero on load.
      // Keep that harmless offset from painting a left fade over the first card.
      const tolerance = window.matchMedia('(max-width: 767.98px)').matches ? 24 : 8;
      const scrollable = maximum > 2;

      shelf.classList.toggle('is-scrollable', scrollable);
      shelf.classList.toggle('can-scroll-left', scrollable && viewport.scrollLeft > tolerance);
      shelf.classList.toggle('can-scroll-right', scrollable && viewport.scrollLeft < maximum - tolerance);

      if (previous) previous.disabled = !scrollable || viewport.scrollLeft <= tolerance;
      if (next) next.disabled = !scrollable || viewport.scrollLeft >= maximum - tolerance;
    }

    function move(direction) {
      const card = viewport.querySelector('.gift-menu-product, .home-product-rail__item, .home-collection-card');
      const track = viewport.querySelector('.home-shelf__track');
      const gap = track ? Number.parseFloat(getComputedStyle(track).gap) || 0 : 0;
      const distance = card ? card.getBoundingClientRect().width + gap : viewport.clientWidth * 0.86;

      viewport.scrollBy({
        left: direction * distance,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragging = false;
    let dragged = false;

    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      dragged = false;
      dragStartX = event.clientX;
      dragStartScroll = viewport.scrollLeft;
    });

    viewport.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      const distance = event.clientX - dragStartX;
      if (!dragged && Math.abs(distance) > 6) {
        dragged = true;
        viewport.setPointerCapture(event.pointerId);
        shelf.classList.add('is-dragging');
      }
      if (!dragged) return;
      viewport.scrollLeft = dragStartScroll - distance;
    });

    function finishDrag(event) {
      if (!dragging) return;
      dragging = false;
      shelf.classList.remove('is-dragging');
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      update();
    }

    viewport.addEventListener('pointerup', finishDrag);
    viewport.addEventListener('pointercancel', finishDrag);
    viewport.addEventListener('click', function (event) {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    }, true);

    previous?.addEventListener('click', function () { move(-1); });
    next?.addEventListener('click', function () { move(1); });
    viewport.addEventListener('scroll', update, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(function () {
        window.requestAnimationFrame(update);
      });
      resizeObserver.observe(viewport);
      shelf.homeShelfResizeObserver = resizeObserver;
    } else {
      window.addEventListener('resize', update, { passive: true });
    }

    update();
  }

  function randomBetween(minimum, maximum) {
    return minimum + Math.random() * (maximum - minimum);
  }

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function shuffle(values) {
    const shuffled = [...values];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    return shuffled;
  }

  function placeMotif(motif, position, hasGsap) {
    const properties = {
      top: `${position.top}%`,
      right: 'auto',
      bottom: 'auto',
      left: `${position.left}%`,
      xPercent: -50,
      yPercent: -50,
      rotation: position.rotation,
      scale: position.scale
    };

    motif.dataset.homeMotifRotation = position.rotation;

    if (hasGsap) {
      window.gsap.set(motif, { ...properties, x: 0, y: 0, opacity: 1 });
      return;
    }

    motif.style.top = properties.top;
    motif.style.right = properties.right;
    motif.style.bottom = properties.bottom;
    motif.style.left = properties.left;
    motif.style.opacity = '1';
    motif.style.transform = `translate(-50%, -50%) rotate(${position.rotation}deg) scale(${position.scale})`;
  }

  const motifDensityPresets = {
    calm: { spacing: 245, mobileSpacing: 210, minimum: 5, mobileMinimum: 4, maximum: 12 },
    standard: { spacing: 165, mobileSpacing: 155, minimum: 7, mobileMinimum: 6, maximum: 24 },
    lively: { spacing: 108, mobileSpacing: 120, minimum: 10, mobileMinimum: 8, maximum: 38 }
  };

  function isVisibleMotif(motif) {
    return window.getComputedStyle(motif).display !== 'none';
  }

  function getMotifTarget(field) {
    const preset = motifDensityPresets[field.dataset.homeMotifDensity] || motifDensityPresets.standard;
    const isMobile = field.clientWidth < 600;
    const spacing = isMobile ? preset.mobileSpacing : preset.spacing;
    const minimum = isMobile ? preset.mobileMinimum : preset.minimum;
    const widthFactor = isMobile ? 1.05 : field.clientWidth < 900 ? 1.25 : 1.65;
    return clamp(Math.ceil((field.clientHeight / spacing) * widthFactor), minimum, preset.maximum);
  }

  function ensureMotifCount(field, target) {
    const seeds = [...field.querySelectorAll('[data-home-motif-seed]')].slice(0, 3);
    let motifs = [...field.querySelectorAll('[data-home-motif]')];
    let visibleCount = motifs.filter(isVisibleMotif).length;
    let cloneIndex = 0;

    while (visibleCount < target && seeds.length) {
      const clone = seeds[cloneIndex % seeds.length].cloneNode(true);
      clone.removeAttribute('data-home-motif-seed');
      clone.dataset.homeMotifClone = 'true';
      field.appendChild(clone);
      motifs.push(clone);
      if (isVisibleMotif(clone)) visibleCount += 1;
      cloneIndex += 1;
    }

    [...motifs].reverse().forEach(function (motif) {
      if (visibleCount <= target || motif.dataset.homeMotifClone !== 'true') return;
      if (isVisibleMotif(motif)) visibleCount -= 1;
      motif.remove();
    });

    return [...field.querySelectorAll('[data-home-motif]')].filter(isVisibleMotif).slice(0, target);
  }

  function placeHeightAwareMotifs(field, motifs, hasGsap) {
    const isMobile = field.clientWidth < 600;
    const isNewsletter = Boolean(field.closest('[data-newsletter-section]'));
    const isHeader = Boolean(field.closest(
      '.collection-header-simple, .content-page-hero, .home-concept-hero, .brand-page-hero, .updates-hero, .article-main__masthead, .not-found-hero'
    ));
    const lanes = isHeader
      ? (isMobile ? [-2, 102] : [3, 11, 89, 97])
      : (isMobile ? (isNewsletter ? [12, 88] : [-1, 101]) : [2, 18, 36, 64, 82, 98]);
    const bandHeight = 94 / Math.max(motifs.length, 1);
    let laneOrder = [];
    let previousLane = null;

    motifs.forEach(function (motif, index) {
      const laneIndex = index % lanes.length;
      if (laneIndex === 0) {
        laneOrder = shuffle(lanes);
        if (laneOrder.length > 1 && laneOrder[0] === previousLane) {
          [laneOrder[0], laneOrder[1]] = [laneOrder[1], laneOrder[0]];
        }
      }

      const lane = laneOrder[laneIndex];
      previousLane = lane;
      placeMotif(motif, {
        top: 3 + bandHeight * (index + 0.5) + randomBetween(-bandHeight * 0.18, bandHeight * 0.18),
        left: lane + randomBetween(-1.2, 1.2),
        rotation: randomBetween(-75, 75),
        scale: randomBetween(0.78, 1.2)
      }, hasGsap);
    });
  }

  function animateMotifs(field, motifs, hasGsap) {
    if (!hasGsap) return;
    window.gsap.killTweensOf(motifs);
    if (reducedMotion.matches) return;

    const motion = field.dataset.homeMotifMotion || 'drift';
    motifs.forEach(function (motif) {
      const baseRotation = Number.parseFloat(motif.dataset.homeMotifRotation) || 0;

      if (motion === 'rise') {
        window.gsap.fromTo(motif, {
          x: 0,
          y: randomBetween(34, 92),
          opacity: randomBetween(0.18, 0.48),
          rotation: baseRotation
        }, {
          x: function () { return window.gsap.utils.random(-30, 30); },
          y: function () { return window.gsap.utils.random(-150, -50); },
          opacity: 0,
          rotation: function () { return baseRotation + window.gsap.utils.random(-12, 12); },
          duration: function () { return window.gsap.utils.random(3, 6); },
          delay: randomBetween(-6, 0),
          repeat: -1,
          repeatRefresh: true,
          ease: 'none'
        });
        return;
      }

      window.gsap.to(motif, {
        x: function () { return window.gsap.utils.random(-10, 10); },
        y: function () { return window.gsap.utils.random(-20, 20); },
        rotation: function () { return baseRotation + window.gsap.utils.random(-15, 15); },
        duration: function () { return window.gsap.utils.random(3.6, 6.8); },
        delay: randomBetween(-6, 0),
        repeat: -1,
        repeatRefresh: true,
        yoyo: true,
        ease: 'sine.inOut'
      });
    });
  }

  function layoutMotifField(field, force) {
    const width = Math.round(field.clientWidth);
    const height = Math.round(field.clientHeight);
    if (!width || !height) return;

    const signature = `${width}:${height}:${field.dataset.homeMotifDensity || 'standard'}`;
    if (!force && field.dataset.homeMotifSignature === signature) return;
    field.dataset.homeMotifSignature = signature;

    const hasGsap = typeof window.gsap !== 'undefined';
    const motifs = ensureMotifCount(field, getMotifTarget(field));
    placeHeightAwareMotifs(field, motifs, hasGsap);
    animateMotifs(field, motifs, hasGsap);
  }

  function initialiseMotifs(scope) {
    const hasGsap = typeof window.gsap !== 'undefined';

    scope.querySelectorAll('[data-home-motif-field]').forEach(function (field) {
      if (field.dataset.homeMotifsReady === 'true') return;
      field.dataset.homeMotifsReady = 'true';
      layoutMotifField(field, true);

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(function () {
          window.requestAnimationFrame(function () { layoutMotifField(field, false); });
        });
        resizeObserver.observe(field);
        field.homeMotifResizeObserver = resizeObserver;
      }
    });

    if (!hasGsap || reducedMotion.matches) return;

    scope.querySelectorAll('[data-home-pattern-field]').forEach(function (field) {
      if (field.dataset.homePatternReady === 'true') return;
      field.dataset.homePatternReady = 'true';
      window.gsap.to(field, {
        backgroundPositionX: '+=120px',
        backgroundPositionY: '-=36px',
        duration: 24,
        repeat: -1,
        ease: 'none'
      });
    });

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    scope.querySelectorAll('[data-home-motif-zone]').forEach(function (zone) {
      if (zone.dataset.homeMotifZoneReady === 'true') return;

      const field = zone.querySelector('[data-home-motif-field]');
      if (!field) return;
      zone.dataset.homeMotifZoneReady = 'true';

      const moveX = window.gsap.quickTo(field, 'x', { duration: 0.9, ease: 'power3.out' });
      const moveY = window.gsap.quickTo(field, 'y', { duration: 0.9, ease: 'power3.out' });

      zone.addEventListener('pointermove', function (event) {
        const bounds = zone.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 18;
        const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 12;
        moveX(x);
        moveY(y);
      }, { passive: true });

      zone.addEventListener('pointerleave', function () {
        moveX(0);
        moveY(0);
      }, { passive: true });
    });
  }

  function initialise(scope) {
    if (scope.matches?.('[data-home-shelf]')) initialiseShelf(scope);
    scope.querySelectorAll('[data-home-hero-slider]').forEach(function (slider) {
      initialiseHero(slider.closest('.home-concept-hero'));
    });
    scope.querySelectorAll('[data-home-shelf]').forEach(initialiseShelf);
    initialiseMotifs(scope);
  }

  document.addEventListener('click', function (event) {
    const link = event.target.closest('[data-home-link]');
    if (!link) return;

    window.dispatchEvent(new CustomEvent('ws:track', {
      detail: {
        type: 'homepage_click',
        payload: {
          section: link.dataset.homeSection || null,
          item: link.dataset.homeItem || null,
          destination: link.getAttribute('href') || null
        }
      }
    }));
  });

  document.addEventListener('DOMContentLoaded', function () { initialise(document); });
  document.addEventListener('shopify:section:load', function (event) { initialise(event.target); });
  document.addEventListener('ws:content:loaded', function (event) {
    initialise(event.detail?.root || document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('.home-concept-hero').forEach(function (hero) {
      hero.homeHeroSwiper?.destroy(true, true);
    });
    event.target.querySelectorAll('[data-home-shelf]').forEach(function (shelf) {
      shelf.homeShelfResizeObserver?.disconnect();
    });
    event.target.querySelectorAll('[data-home-motif-field]').forEach(function (field) {
      field.homeMotifResizeObserver?.disconnect();
      if (typeof window.gsap !== 'undefined') {
        window.gsap.killTweensOf(field.querySelectorAll('[data-home-motif]'));
      }
    });
  });
})();
